import type { CachePersistenceConfig } from './selective-cache-persistence';
import { PERSISTENT_OPERATIONS } from '../constants';

/**
 * Configuration for entities that should be persisted in the GraphQL cache
 * Customize this based on your application's needs
 */
export const CACHE_PERSISTENCE_CONFIG: CachePersistenceConfig = {
  // Entity types to persist (by __typename from GraphQL schema)
  // Be selective here - only persist stable, frequently accessed entities
  persistentTypes: [
    'Vendor',
    'User',
    'ProcessorSettings',

    // Reference data that doesn't change often
    'CancellationReason',

    // User-specific settings (but be careful about size)
    'UserSettings',
  ],

  // Query operation names to persist (specific queries to cache)
  // Using shared constant to ensure consistency with authentication
  persistentOperations: [...PERSISTENT_OPERATIONS],

  // Cache TTL settings - reduced to prevent stale data
  ttl: 10 * 60 * 1000, // 2 hours for most data

  // Max cache size (reduced to 1MB to prevent bloating)
  maxSize: 1 * 1024 * 1024,
};

/**
 * Configuration for different cache TTL based on entity type
 * More frequently changing data gets shorter TTL
 */
export const ENTITY_TTL_CONFIG: Record<string, number> = {
  // User data - moderate TTL since it can change
  User: 60 * 60 * 1000, // 1 hours
  UserSettings: 15 * 60 * 1000, // 15 minutes

  // Processing partner data - longer TTL since it's relatively stable
  Vendor: 60 * 60 * 1000, // 1 hours
  ProcessorSettings: 15 * 60 * 1000, // 15 minutes
  ProcessorCapability: 15 * 60 * 1000, // 15 minutes

  // Reference data - very long TTL since it rarely changes
  CancellationReason: 7 * 24 * 60 * 60 * 1000, // 7 days
};

/**
 * Development configuration with shorter TTLs for testing
 */
export const DEV_CACHE_PERSISTENCE_CONFIG: CachePersistenceConfig = {
  ...CACHE_PERSISTENCE_CONFIG,
  ttl: 10 * 60 * 1000, // 10 minutes in development
  maxSize: 512 * 1024, // 512KB in development for easier debugging
};
